import * as turf from '@turf/turf'
import type { Feature, Polygon } from 'geojson'
import type { LngLatBoundsLike } from 'maplibre-gl'
import { TRADE_AREA_RINGS } from '../config/mapConfig'

export interface TradeAreaRing {
	radiusMiles: number
	polygon: Feature<Polygon>
	center: [number, number]
	siteId: string
	color: string
}

export interface TradeAreaOverlap {
	polygon: Feature<Polygon>
	siteIds: [string, string]
	radiusMiles: number
	overlapAreaSqMiles: number
}

const SQ_METERS_TO_SQ_MILES = 3.861e-7
const MIN_OVERLAP_SQ_MILES = 0.01
const US_BBOX_FALLBACK: LngLatBoundsLike = [
	[-125, 24],
	[-66, 49],
]

export const tradeAreaService = {
	generateRings(
		center: [number, number],
		siteId: string,
		color: string,
		radii: number[] = TRADE_AREA_RINGS,
	): TradeAreaRing[] {
		return radii.map((radius) => {
			const circle = turf.circle(center, radius, {
				steps: 64,
				units: 'miles',
				properties: { siteId, color, radiusMiles: radius },
			})
			return {
				radiusMiles: radius,
				polygon: circle as Feature<Polygon>,
				center,
				siteId,
				color,
			}
		})
	},

	calculateOverlap(ringsA: TradeAreaRing[], ringsB: TradeAreaRing[]): TradeAreaOverlap[] {
		const results: TradeAreaOverlap[] = []
		for (const a of ringsA) {
			for (const b of ringsB) {
				if (a.radiusMiles !== b.radiusMiles) continue
				try {
					const fc = turf.featureCollection([a.polygon, b.polygon])
					const intersection = turf.intersect(fc) as Feature<Polygon> | null
					if (!intersection) continue
					const sqMeters = turf.area(intersection)
					const sqMiles = sqMeters * SQ_METERS_TO_SQ_MILES
					if (sqMiles <= MIN_OVERLAP_SQ_MILES) continue
					results.push({
						polygon: intersection,
						siteIds: [a.siteId, b.siteId],
						radiusMiles: a.radiusMiles,
						overlapAreaSqMiles: sqMiles,
					})
				} catch {
					// turf.intersect can throw on invalid geometries — skip silently
				}
			}
		}
		return results
	},

	getBoundsForSites(sites: Array<{ longitude: number; latitude: number }>): LngLatBoundsLike {
		if (sites.length === 0) return US_BBOX_FALLBACK
		const points = turf.featureCollection(
			sites.map((s) => turf.point([s.longitude, s.latitude])),
		)
		const bbox = turf.bbox(points)
		return [
			[bbox[0], bbox[1]],
			[bbox[2], bbox[3]],
		]
	},

	/**
	 * Like `getBoundsForSites`, but inflates each site to a circle of the
	 * largest configured trade-area radius first, so the resulting bounds
	 * frame the outermost ring rather than clipping it. Used for initial
	 * map fits where the rings are visible.
	 */
	getBoundsForSitesWithRings(
		sites: Array<{ longitude: number; latitude: number }>,
		radii: number[] = TRADE_AREA_RINGS,
	): LngLatBoundsLike {
		if (sites.length === 0) return US_BBOX_FALLBACK
		const maxRadius = Math.max(...radii)
		// Few steps is fine — we only care about the bbox, not the rendered
		// circle. 16 segments matches the outer-ring extents to within
		// sub-pixel error at typical map zooms.
		const circles = turf.featureCollection(
			sites.map((s) =>
				turf.circle([s.longitude, s.latitude], maxRadius, {
					steps: 16,
					units: 'miles',
				}),
			),
		)
		const bbox = turf.bbox(circles)
		return [
			[bbox[0], bbox[1]],
			[bbox[2], bbox[3]],
		]
	},

	distanceBetween(a: [number, number], b: [number, number]): number {
		return turf.distance(turf.point(a), turf.point(b), { units: 'miles' })
	},

	/**
	 * The reach of the widest ring currently drawn, or null when none is.
	 *
	 * Used to bound what the Other Retailers layer and its sidebar list show.
	 * Null is the important case and means "do not narrow": rings narrow, they
	 * do not gate, so an empty selection shows everything saved rather than
	 * nothing. Returning 0 here would silently empty both.
	 */
	outermostRadius(enabledRadii: number[]): number | null {
		return enabledRadii.length > 0 ? Math.max(...enabledRadii) : null
	},
}
